import { createSelector } from "reselect";

import { getMasterRuleSets, getRuleSets } from "./root";

import {
  selectMasterRuleSetIdsByMasterRuleBookIdLookup,
  selectRuleSetIdsByMasterRuleSetIdLookup,
  selectRuleSetIdsByRuleBookIdLookup,
} from "./indexes";
import { createLookupCombiner, createLookupSelectors } from "./lib";

export const getRuleSetById = (ruleSetId: string) => (state: RootState) =>
  getRuleSets(state)[ruleSetId] || null;
export const getMasterRuleSetById = (ruleSetId: string) => (state: RootState) =>
  getMasterRuleSets(state)[ruleSetId] || null;

export const getMasterRuleSetIdsByMasterRuleBookId =
  (masterRuleBookId: string) => (state: RootState) =>
    selectMasterRuleSetIdsByMasterRuleBookIdLookup(state)[masterRuleBookId] ||
    null;

type RuleSetLike = RuleSet | MasterRuleSet;

function ruleSetOptionsReducer<RuleSetVariant extends RuleSetLike>(
  ruleSetVariantByIdLookup: Record<string, RuleSetVariant>,
) {
  return Object.values(ruleSetVariantByIdLookup).map(ruleSet => ({
    label: ruleSet.name || `(Unnamed Rule Set ) ${ruleSet.input_field}`,
    value: ruleSet.id,
  }));
}

export const getRuleSetOptions = createSelector(
  [getRuleSets],
  ruleSetOptionsReducer,
);
export const getMasterRuleSetOptions = createSelector(
  [getMasterRuleSets],
  ruleSetOptionsReducer,
);

export const [
  selectMasterRuleSetsByMasterRuleBookIdLookup,
  getMasterRuleSetsByMasterRuleBookId,
] = createLookupSelectors(
  [selectMasterRuleSetIdsByMasterRuleBookIdLookup, getMasterRuleSets],
  createLookupCombiner((masterRuleSetIds, masterRuleSetByIdLookup) =>
    masterRuleSetIds.map(
      masterRuleSetId => masterRuleSetByIdLookup[masterRuleSetId],
    ),
  ),
);

export const [selectRuleSetsByRuleBookIdLookup, getRuleSetsByRuleBookId] =
  createLookupSelectors(
    [selectRuleSetIdsByRuleBookIdLookup, getRuleSets],
    createLookupCombiner((ruleSetIds, ruleSetByIdLookup) =>
      ruleSetIds.map(ruleId => ruleSetByIdLookup[ruleId]),
    ),
  );

export const [selectRuleSetOptionsByRuleBookId, getRuleSetOptionsByRuleBookId] =
  createLookupSelectors(
    [selectRuleSetIdsByRuleBookIdLookup, getRuleSetOptions],
    createLookupCombiner((ruleSetIds, ruleSetOptions) =>
      ruleSetIds.map(ruleSetId =>
        ruleSetOptions.find(option => option.value === ruleSetId),
      ),
    ),
  );
export const [
  selectMasterRuleSetOptionsByMasterRuleBookId,
  getMasterRuleSetOptionsByMasterRuleBookId,
] = createLookupSelectors(
  [selectMasterRuleSetIdsByMasterRuleBookIdLookup, getMasterRuleSetOptions],
  createLookupCombiner((ruleSetIds, ruleSetOptions) =>
    ruleSetIds.map(ruleSetId =>
      ruleSetOptions.find(option => option.value === ruleSetId),
    ),
  ),
);

export const getRuleSetIdsByRuleBookId =
  (ruleBookId: string) => (state: RootState) =>
    selectRuleSetIdsByRuleBookIdLookup(state)[ruleBookId] || null;

export const getRuleSetIdsByMasterRuleSetId =
  (masterRuleSetId: string) => (state: RootState) =>
    selectRuleSetIdsByMasterRuleSetIdLookup(state)[masterRuleSetId] || null;
